// import constants
import { FVY_DOMAIN, SITE } from "@/config/constants";
// import helpers
import { convertRatingToStars } from "@/helpers";
// import selectors
import * as selectors from "@/selectors";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { includes, path } from "ramda";
import { useSelector } from "react-redux";

const styles = {
	trustPilotContainer: {
		base: css`
			margin-top: 20px;
			text-align: center;
			min-width: 118px;
			max-width: 120px;

			@media (max-width: 991px) {
				margin-top: 0;
			}
		`,
		domainLevelOverrides: {
			[FVY_DOMAIN]: {
				base: css`
					align-self: end;

					@media (max-width: 991px) and (min-width: 581px) {
						margin-right: 5rem;
					}
				`,
				software: css`
					align-self: center;
				`,
			},
		},
	},
	reviewCount: css`
		font-size: 14px;
		color: #232227;

		@media (max-width: 991px) {
			text-align: center;
		}

		@media (max-width: 580px) {
			font-size: 13px;
		}

		span {
			font-weight: 700;
			font-family: Graphik Web;
		}
	`,

	ratingContainer: css`
		justify-content: center;
		display: flex;
		margin: 4px 0;
	`,

	ratings: {
		baseStyles: css`
			width: 21px;
			height: 21px;
			margin-right: 3px;
			border-radius: 3px;
			display: flex;
			align-items: center;
			position: relative;

			@media (max-width: 580px) {
				width: 18px;
				height: 18px;
			}
		`,
		default: css`
			background: #d9d9d9;
		`,
		poor: css`
			background: #ec4b34;
		`,
		fair: css`
			background: #f18b3f;
		`,
		average: css`
			background: #f9ce47;
		`,
		good: css`
			background: #8acb41;
		`,
		excellent: css`
			background: #51b37f;
		`,
		halfStar: css`
			width: 10.5px;
			height: 21px;
			background-color: #d9d9d9;
			border-radius: 0 3px 3px 0;
			position: absolute;
			left: 50%;
			top: 0;

			@media (max-width: 580px) {
				width: 9px;
				height: 18px;
			}
		`,
	},
	star: css`
		position: absolute;
		left: 4px;
		top: 3px;

		@media (max-width: 580px) {
			max-width: 12px;
			left: 3px;
		}
	`,
	ratingFooter: css`
		display: flex;
		justify-content: center;

		span {
			display: flex;
			align-items: flex-end;
			width: 100%;
			justify-content: space-evenly;
		}

		p {
			font-size: 14px;
			color: #232227;
			line-height: 1;
			margin: 0;
			font-weight: 700;
		}
	`,
	noReviews: css`
		font-size: 14px;
		color: #232227;
		line-height: 1;
		margin: 0;
		font-weight: 600;
	`,
};

const TO_TRIM = "Half";
const STAR_SVG = (
	<svg
		css={styles.star}
		height="13"
		viewBox="0 0 13 13"
		width="13"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="m453.508 578.906 2.822-.756 1.179 3.85zm6.492-4.913h-4.967l-1.527-5.008-1.527 5.008h-4.967l4.02 3.011-1.527 4.95 4.021-3.068 2.474-1.886 4-3.011z"
			fill="#fff"
			fillRule="evenodd"
			transform="translate(-447 -569)"
		/>
	</svg>
);

const getTrustpilotLogo = (rating) => {
	const ratingElement = rating ? <p>{rating}</p> : null;
	return (
		<div css={styles.ratingFooter}>
			<span>
				{ratingElement}
				<p
					css={css`
						font-weight: normal !important;
					`}
				>
					on
				</p>
				<svg
					height="16"
					viewBox="20.5 248.5 600 147.3"
					width="67"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path d="m178.2 300.7h60.7v11.3h-23.9v63.7h-13.1v-63.7h-23.8zm58.1 20.7h11.2v10.5h.2c.4-1.5 1.1-2.9 2.1-4.3s2.2-2.7 3.7-3.8c1.4-1.2 3-2.1 4.8-2.8 1.7-.7 3.5-1.1 5.3-1.1 1.4 0 2.4.1 2.9.1.5.1 1.1.2 1.6.2v11.5c-.8-.2-1.7-.3-2.6-.4s-1.7-.2-2.6-.2c-2 0-3.9.4-5.7 1.2s-3.3 2-4.7 3.5c-1.3 1.6-2.4 3.5-3.2 5.8s-1.2 5-1.2 8v25.8h-12zm86.8 54.3h-11.8v-7.6h-.2c-1.5 2.8-3.7 4.9-6.6 6.6s-5.9 2.5-8.9 2.5c-7.1 0-12.3-1.7-15.5-5.3-3.2-3.5-4.8-8.9-4.8-16v-34.5h12v33.3c0 4.8.9 8.2 2.8 10.1 1.8 2 4.4 3 7.7 3 2.5 0 4.6-.4 6.3-1.2s3.1-1.8 4.1-3.1c1.1-1.3 1.8-2.9 2.3-4.7s.7-3.8.7-5.9v-31.5h12v54.3zm20.4-17.4c.4 3.5 1.7 5.9 4 7.4 2.3 1.4 5.1 2.1 8.3 2.1 1.1 0 2.4-.1 3.8-.3s2.8-.5 4-1c1.3-.5 2.3-1.2 3.1-2.2s1.2-2.2 1.1-3.7-.6-2.8-1.7-3.8-2.4-1.7-4-2.4c-1.6-.6-3.5-1.1-5.6-1.5s-4.2-.9-6.4-1.4-4.4-1.1-6.5-1.8-3.9-1.6-5.6-2.9c-1.6-1.2-3-2.7-3.9-4.6-1-1.9-1.5-4.1-1.5-6.9 0-3 .7-5.4 2.2-7.4 1.4-2 3.3-3.6 5.5-4.8s4.7-2.1 7.4-2.6 5.3-.7 7.7-.7c2.8 0 5.5.3 8 .9s4.9 1.5 6.9 2.9c2.1 1.3 3.8 3.1 5.1 5.2 1.4 2.1 2.2 4.7 2.6 7.7h-12.5c-.6-2.9-1.9-4.8-3.9-5.8-2.1-1-4.4-1.5-7.1-1.5-.8 0-1.9.1-3 .2-1.2.2-2.2.4-3.3.8-1 .4-1.9 1-2.6 1.7s-1.1 1.7-1.1 2.9c0 1.5.5 2.6 1.5 3.5s2.3 1.6 4 2.3c1.6.6 3.5 1.1 5.6 1.5s4.3.9 6.5 1.4 4.3 1.1 6.4 1.8 4 1.6 5.6 2.9c1.6 1.2 3 2.7 4 4.5s1.5 4.1 1.5 6.7c0 3.2-.7 5.9-2.2 8.2-1.5 2.2-3.4 4.1-5.7 5.5s-5 2.4-7.8 3.1c-2.9.6-5.7 1-8.5 1-3.4 0-6.6-.4-9.5-1.2s-5.5-2-7.6-3.5c-2.1-1.6-3.8-3.5-5-5.9-1.2-2.3-1.9-5.1-2-8.4h12.1v.1zm39.5-36.9h9.1v-16.4h12v16.3h10.8v8.9h-10.8v29c0 1.3.1 2.3.2 3.3.1.9.4 1.7.7 2.3.4.6 1 1.1 1.7 1.4.8.3 1.8.5 3.2.5.8 0 1.7 0 2.5-.1s1.7-.2 2.5-.4v9.3c-1.3.2-2.6.3-3.9.4-1.3.2-2.5.2-3.9.2-3.2 0-5.7-.3-7.6-.9s-3.4-1.5-4.5-2.6c-1.1-1.2-1.8-2.6-2.2-4.3s-.6-3.8-.7-6v-32h-9.1v-9.1zm40.3 0h11.3v7.4h.2c1.7-3.2 4-5.4 7-6.8s6.2-2.1 9.8-2.1c4.3 0 8 .7 11.2 2.3 3.2 1.5 5.8 3.5 7.9 6.2 2.1 2.6 3.7 5.7 4.7 9.2s1.6 7.3 1.6 11.2c0 3.7-.5 7.2-1.4 10.6-1 3.4-2.4 6.5-4.3 9.1s-4.3 4.7-7.3 6.3-6.4 2.4-10.4 2.4c-1.7 0-3.5-.2-5.2-.5s-3.4-.8-5-1.5-3.1-1.6-4.4-2.7c-1.4-1.1-2.5-2.4-3.4-3.8h-.2v27.1h-12v-74.4zm41.9 27.2c0-2.4-.3-4.8-1-7.1-.6-2.3-1.6-4.3-2.9-6.1s-2.9-3.2-4.7-4.3c-1.9-1.1-4.1-1.6-6.5-1.6-5 0-8.8 1.7-11.4 5.2-2.5 3.5-3.8 8.2-3.8 14 0 2.8.3 5.3 1 7.6s1.6 4.3 3 6c1.3 1.7 2.9 3 4.8 4s4 1.5 6.5 1.5c2.8 0 5-.6 6.9-1.7s3.4-2.6 4.7-4.3c1.2-1.8 2.1-3.8 2.6-6.1.5-2.4.8-4.7.8-7.1zm21.1-47.9h12v11.3h-12zm0 20.7h12v54.3h-12zm22.7-20.7h12v75h-12zm48.6 76.5c-4.3 0-8.2-.7-11.6-2.2s-6.2-3.4-8.6-5.9c-2.3-2.5-4.1-5.6-5.3-9.1s-1.9-7.4-1.9-11.5.6-7.9 1.9-11.4c1.2-3.5 3-6.5 5.3-9.1 2.3-2.5 5.2-4.5 8.6-5.9s7.3-2.2 11.6-2.2 8.2.7 11.6 2.2c3.4 1.4 6.2 3.4 8.6 5.9 2.3 2.5 4.1 5.6 5.3 9.1s1.9 7.3 1.9 11.4c0 4.2-.6 8-1.9 11.5s-3 6.5-5.3 9.1c-2.3 2.5-5.2 4.5-8.6 5.9s-7.2 2.2-11.6 2.2zm0-9.5c2.6 0 5-.6 6.9-1.7 2-1.1 3.5-2.6 4.8-4.4s2.2-3.9 2.8-6.1c.6-2.3.9-4.6.9-7 0-2.3-.3-4.6-.9-6.9s-1.5-4.3-2.8-6.1-2.9-3.2-4.8-4.3c-2-1.1-4.3-1.7-6.9-1.7s-5 .6-6.9 1.7c-2 1.1-3.5 2.6-4.8 4.3-1.3 1.8-2.2 3.8-2.8 6.1s-.9 4.6-.9 6.9c0 2.4.3 4.7.9 7s1.5 4.3 2.8 6.1 2.9 3.3 4.8 4.4c2 1.2 4.3 1.7 6.9 1.7zm31-46.3h9.1v-16.4h12v16.3h10.8v8.9h-10.8v29c0 1.3.1 2.3.2 3.3.1.9.4 1.7.7 2.3.4.6 1 1.1 1.7 1.4.8.3 1.8.5 3.2.5.8 0 1.7 0 2.5-.1s1.7-.2 2.5-.4v9.3c-1.3.2-2.6.3-3.9.4-1.3.2-2.5.2-3.9.2-3.2 0-5.7-.3-7.6-.9s-3.4-1.5-4.5-2.6c-1.1-1.2-1.8-2.6-2.2-4.3s-.6-3.8-.7-6v-32h-9.1v-9.1z" />
					<path
						d="m164.2 300.7h-54.9l-16.9-52.2-17 52.2-54.9-.1 44.4 32.3-17 52.2 44.4-32.3 44.4 32.3-16.9-52.2z"
						fill="#00b67a"
					/>
					<path d="m123.6 344.7-3.8-11.8-27.4 19.9z" fill="#005128" />
				</svg>
			</span>
		</div>
	);
};

const NO_REVIEWS = (
	<div
		css={[
			styles.trustPilotContainer.base,
			path(["trustPilotContainer", "domainLevelOverrides", SITE], styles),
		]}
	>
		<p css={styles.noReviews}>No Reviews Yet</p>
		{getTrustpilotLogo()}
	</div>
);

export const TrustpilotReviewWidget = ({ company }) => {
	const isTrustpilotRendered = useSelector(
		selectors.getIsTrustpilotWidgetRendered,
	);
	const usesSoftwareDesign = useSelector(selectors.getUsesSoftwareDesign);

	if (!isTrustpilotRendered) return null;

	const reviewCount = useSelector((state) =>
		selectors.getTrustpilotReviewCount(state, { company }),
	);
	const reviewScore = useSelector((state) =>
		selectors.getTrustpilotScore(state, { company }),
	);

	/* Throw a warning if a necessary prop isn't passed (Still reported to Sentry, however it won't blow up Slack with unnecessary noise) */
	if (!reviewCount) {
		console.warn("reviewCount prop not passed to <TrustpilotReviewWidget>");
	}
	if (!reviewScore) {
		console.warn("reviewRating prop not passed to <TrustpilotReviewWidget>");
	}

	/* Exit early in the event of missing props or reviewCount === 0 */
	if (!reviewCount || reviewCount === 0 || !reviewScore) return NO_REVIEWS;

	const textualRating = convertRatingToStars(reviewScore);

	const ratingStars = textualRating.map((quality, index) => {
		// if half star is needed, then trim identifier to match styling and render span
		const halfStarBool = includes(TO_TRIM, quality);
		if (halfStarBool) {
			const qualityTrimmed = quality.substring(
				0,
				quality.length - TO_TRIM.length,
			);
			return (
				<div
					key={index}
					css={[styles.ratings.baseStyles, styles.ratings[qualityTrimmed]]}
				>
					<span css={[styles.ratings.halfStar]}></span>
					{STAR_SVG}
				</div>
			);
		} else {
			return (
				<div
					key={index}
					css={[styles.ratings.baseStyles, styles.ratings[quality]]}
				>
					{STAR_SVG}
				</div>
			);
		}
	});

	return (
		<div
			css={[
				styles.trustPilotContainer.base,
				path(
					[
						"trustPilotContainer",
						"domainLevelOverrides",
						SITE,
						usesSoftwareDesign ? "software" : "base",
					],
					styles,
				),
			]}
		>
			<div css={styles.reviewCount}>
				<span>{reviewCount.toLocaleString()}</span> Reviews
			</div>
			<div css={styles.ratingContainer}>{ratingStars}</div>
			{getTrustpilotLogo(reviewScore)}
		</div>
	);
};

export default TrustpilotReviewWidget;

TrustpilotReviewWidget.propTypes = {
	company: PropTypes.object.isRequired,
};
